import { queryOptions, skipToken } from '@tanstack/react-query';

import { MedicalCaseData } from 'chaika-frontend-types/frontend-cms';
import { MedicalCaseStatus } from 'chaika-enums';

import { MedicalCaseApi } from '@api/MedicalCaseApi';
import { STALE_TIME_ONE_MINUTE_MS } from '@utils/staleTime';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';

interface MedicalCaseListFilters {
    ehrIds?: string[];
    entryId?: string;
}

interface ByManagerFilters {
    managerId: string;
    status?: MedicalCaseStatus;
}

interface HistoryFilters {
    ehrId: string | undefined;
    id: string;
}

// TODO: move to @patient/queries together with ../mutations
export const medicalCaseKeys = {
    all: ['medicalCases'] as const,
    lists: () => [...medicalCaseKeys.all, 'list'] as const,
    list: (filters: MedicalCaseListFilters) =>
        [...medicalCaseKeys.lists(), { filters: normalizeFilters(filters) }] as const,
    byManager: ({ managerId, status }: ByManagerFilters) =>
        [...medicalCaseKeys.lists(), { managerId, status }] as const,
    byIds: (ids: string[]) => [...medicalCaseKeys.all, 'byIds', getUniqSortedIds(ids)] as const,
    detail: (id: string) => [...medicalCaseKeys.all, 'detail', id] as const,
    history: ({ ehrId, id }: HistoryFilters) => [...medicalCaseKeys.all, 'history', { ehrId, id }] as const,
};

export const medicalCaseQueryOptions = {
    list: ({ ehrIds, entryId }: MedicalCaseListFilters) =>
        queryOptions({
            // Episode mutations go through MobX services without RQ invalidation: refetch on every mount.
            staleTime: 0,
            queryKey: medicalCaseKeys.list({ ehrIds, entryId }),
            queryFn: () => MedicalCaseApi.find({ ehrId: ehrIds?.join(','), entryId }),
            enabled: (ehrIds?.length ?? 0) > 0,
        }),
    byManager: ({ managerId, status }: ByManagerFilters) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: medicalCaseKeys.byManager({ managerId, status }),
            queryFn: managerId ? () => MedicalCaseApi.find({ managerId, status }) : skipToken,
        }),
    byIds: (ids: string[]) =>
        queryOptions({
            // Episode mutations go through MobX services without RQ invalidation: refetch on every mount.
            staleTime: 0,
            queryKey: medicalCaseKeys.byIds(ids),
            queryFn: () =>
                ids.length > 0
                    ? MedicalCaseApi.getByIds(getUniqSortedIds(ids))
                    : Promise.resolve<MedicalCaseData[]>([]),
            enabled: ids.length > 0,
        }),
    detail: (id: string) =>
        queryOptions({
            // Мутации эпизода идут через MobX-сервисы без инвалидации RQ: при каждом заходе нужен свежий статус.
            staleTime: 0,
            queryKey: medicalCaseKeys.detail(id),
            queryFn: () => MedicalCaseApi.getById(id),
        }),
    history: ({ ehrId, id }: HistoryFilters) =>
        queryOptions({
            staleTime: STALE_TIME_ONE_MINUTE_MS,
            queryKey: medicalCaseKeys.history({ ehrId, id }),
            queryFn: ehrId ? () => MedicalCaseApi.getMedicalCaseHistory({ ehrId, id }) : skipToken,
        }),
};

function normalizeFilters({ ehrIds, entryId }: MedicalCaseListFilters): MedicalCaseListFilters {
    return {
        ehrIds: ehrIds ? [...ehrIds].sort() : undefined,
        entryId,
    };
}
